import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import DropdownIcon from '~/icons/dropdown';
import { NavContext } from '~/contexts/NavContext';
import { AppDataContext } from '~/contexts/AppDataContext';
import type { NavItemType } from '~/types/NavItemType';
import type { ThemeVariantsType } from '~/types/Utilities/ThemeVariantsType';
import { getCSSFromTheme } from '~/utilities/getCSSFromTheme';
import type { FACTORS } from '~/types/AppData/Theme/ThemeComponentFieldInterface';
import { useIsSI } from '~/utilities/useIsSI';

const iconClasses = cva('inline-block ml-[5px] transition duration-300', {
  variants: {
    level: {
      0: 'nav-item-link group-hover/topnav:rotate-180',
      1: 'nav-item-sublink group-hover/dropdown:rotate-180',
    },
  },
});

const navItemLink = cva(
  'w-full h-full flex items-center box-border cursor-pointer',
  {
    variants: {
      level: {
        0: 'nav-item-link',
        1: 'nav-item-sublink text-wrap',
      },
      underline: {
        true: 'hover:underline',
        false: '',
      },
    },
  }
);

export default component$(
  ({ item, activeItem, level }: NavItemType): JSX.Element => {
    const {
      payloadToSSR: { isMobileViewer },
      themeComponents: { header },
    } = useContext(AppDataContext);
    const { theme, navInMobile, isDivisionsDropdown } = useContext(NavContext);
    const clampedLevel = level >= 1 ? 1 : 0;
    const isSI = useIsSI();

    const itemKey = item.link.text + item.link.url;
    const navInMobileAndMobile = !!isMobileViewer && !!navInMobile;
    const isActive =
      !!(navInMobileAndMobile || isDivisionsDropdown) &&
      activeItem.value === itemKey;

    const themeMapParent: ThemeVariantsType = {
      color: isActive
        ? theme.LinkColorTypeHover.value
        : theme.LinkColorTypeNormal.value,
      hoverColor: !navInMobileAndMobile
        ? theme.LinkColorTypeHover.value
        : theme.LinkColorTypeNormal.value,
    };

    if (level < 1) {
      themeMapParent.sizeSm = theme.LinkFontSizeSmall.value;
      themeMapParent.sizeMd = theme.LinkFontSizeMedium.value;
      themeMapParent.sizeLg = theme.LinkFontSizeLarge.value;
      themeMapParent.border = theme.LinkHoverBorderBottom?.value;
      themeMapParent.borderBottomHover = theme.LinkHoverBorderBottomWidth
        ?.value as Extract<FACTORS, 0 | 0.1 | 0.25>;
      themeMapParent.paddingTopHover = theme.LinkHoverBorderBottomWidth
        ?.value as Extract<FACTORS, 0 | 0.1 | 0.25>;
    } else {
      themeMapParent.sizeSm = header.hamburgerMenuSubLinksFontSizeSmall.value;
    }

    const linkClass = getCSSFromTheme(
      themeMapParent,
      navItemLink({ level: 0, underline: theme.LinkHoverUnderline?.value })
    );

    return (
      <span
        class='flex items-center cursor-pointer'
        onClick$={() => (activeItem.value = isActive ? '' : itemKey)}
      >
        <h4 class={linkClass}>{item.link.text}</h4>
        <span
          class={iconClasses({
            level: clampedLevel,
          })}
        >
          <DropdownIcon
            active={isActive}
            type={isSI ? 'chevron' : 'triangle'}
            color={theme.LinkDropdownIconColor.value}
            activeColor={theme.LinkColorTypeHover.value}
          />
        </span>
      </span>
    );
  }
);
